
import { defineComponent, watch, ref, computed, onUnmounted } from "vue";

import { RouterLink, useRoute, useRouter } from "vue-router";
import { vLoading, ElMessage } from "element-plus";

import { online_link_name } from "@/basics/enums";
import markdown from "@/markdown/renderMD";

import { StyleTag, UserTag } from "@/components/common";
import ImageWall from "@/components/common/ImageWall.vue";
import ArticleListItem from "@/components/ArticleListItem";
import MetaArtistTab from "./MetaArtistTab.vue";
import { AlbumMetaArtist as MetaArtistAlbum } from "@/components/EntityListItem";
import { AlbumModel, ArticleModel, ArtistId, ArtistModel, ArticleType } from "@/typings";
import { countViewArtist, createArticleDraft, do_lock, getArtistInfo } from "@/services";
import { useUserInfo } from "@/store";
import ArtistEditor from "@/components/Editor/ArtistEdit.vue";
import AlbumEdit from "@/components/Editor/AlbumEdit.vue";
import { CommentList } from "@/components/Comment";

import wx from "@/basics/wx";
import ajax from "@/basics/fetch";
import wechatTitle from "@/directives/wechat-title";
type AlbumType = "a0" | "a1" | "a6" | "a20" | "asingle";

export default defineComponent({
    directives: {
        vLoading,
        wechatTitle,
    },
    components: {
        RouterLink,
        StyleTag,
        UserTag,
        ImageWall,
        ArticleListItem,
        MetaArtistTab,
        MetaArtistAlbum,
        ArtistEditor,
        AlbumEdit,
        CommentList,
    },
    setup() {
        const route = useRoute();
        const router = useRouter();
        const userInfo = useUserInfo();
        const id = ref("" as ArtistId);
        const loading = ref(false);
        const albums = ref<Record<AlbumType, AlbumModel[]>>({
            a0: [],
            asingle: [],
            a1: [],
            a6: [],
            a20: [],
        });
        const activeAlbumType = ref<AlbumType>("a0");
        const info = ref<ArtistModel | null>(null);
        const articles = ref<ArticleModel[]>([]);
        const handlerTimeout = ref(0);
        // 编辑器
        const albumEditor = ref();

        const refreshData = (paramId: ArtistId) => {
            if (!paramId) {
                return;
            }
            id.value = paramId;
            loading.value = true;
            getArtistInfo(paramId)
                .then((json) => {
                    info.value = json.data.info;
                    albums.value = {
                        a0: [],
                        asingle: [],
                        a1: [],
                        a6: [],
                        a20: [],
                    };
                    json.data.albums.forEach((album) => {
                        if (album.type.index === 2) {
                            albums.value.asingle.push(album);
                        } else if (album.album_type.index === 0) {
                            albums.value.a0.push(album);
                        } else if (album.album_type.index === 1) {
                            albums.value.a1.push(album);
                        } else if (album.album_type.index === 6) {
                            albums.value.a6.push(album);
                        } else {
                            albums.value.a20.push(album);
                        }
                    });

                    albums.value.a20.push(...json.data.related_albums);
                    for (const k in info.value.styles) {
                        if (json.data.style_hot[info.value.styles[k]._id]) {
                            info.value.styles[k].hot_index =
                                json.data.style_hot[info.value.styles[k]._id];
                        } else {
                            info.value.styles[k].hot_index = 0;
                        }
                    }
                    articles.value = json.data.articles;
                    wx.wxConfig(ajax, {
                        title: info.value.name,
                        desc: info.value.styles
                            .map((style) => {
                                if (style.name) {
                                    return style.name;
                                } else {
                                    return style.name_orig;
                                }
                            })
                            .join("/"),
                        imgUrl: info.value.primary_img,
                    });

                    handlerTimeout.value = window.setTimeout(() => {
                        window.clearTimeout(handlerTimeout.value);
                        handlerTimeout.value = 0;
                        countViewArtist(id.value);
                    }, 5000);
                })
                .finally(() => {
                    loading.value = false;
                });
        };
        onUnmounted(() => {
            if (handlerTimeout.value) {
                window.clearTimeout(handlerTimeout.value);
            }
        });

        watch(
            () => {
                return route.params.id as ArtistId;
            },
            refreshData,
            {
                immediate: true,
            },
        );

        const refresh = () => {
            refreshData(route.params.id as ArtistId);
        };

        const handleShowThirdEditor = () => {
            dialogFormAlbumVisible.value = true;
            albumEditor.value.isCombine = 2;
        };

        const handlerCreateArticle = () => {
            if (info.value!.quality.index === 0) {
                ElMessage.error("请先协助完善艺人信息");
                return;
            }
            let flag = 0;
            for (const k in info.value!.online_links) {
                if ((info.value!.online_links as any)[k].url !== "") {
                    flag = 1;
                    break;
                }
            }
            if (flag === 0) {
                ElMessage.error("请先协助完善艺人信息，确保至少有一个试听链接");
                return;
            }

            createArticleDraft({
                type: "interviews",
                reference_id: info.value!._id,
            })
                .then((json) => {
                    router.push({
                        path:
                            "/article/write/interviews/" +
                            info.value!._id +
                            "/" +
                            json.data.article_id,
                    });
                })
                .catch((json) => {
                    ElMessage.error(json.data.msg);
                });
        };

        const dialogFormVisible = ref(false);
        const showEditer = () => {
            dialogFormVisible.value = true;
        };

        const doLockInfo = () => {
            loading.value = true;
            do_lock("artist", id.value)
                .then((json) => {
                    Object.assign(info.value!, json.data.info);
                })
                .catch((json) => {
                    ElMessage.error(json.data.msg);
                })
                .finally(() => {
                    loading.value = false;
                });
        };

        const dialogFormAlbumVisible = ref(false);

        return {
            id,
            info,
            articles,
            albums,
            albumEditor,
            activeAlbumType,
            online_link_name,
            markdown,
            userInfo,
            handlerCreateArticle,
            handleShowThirdEditor,
            dialogFormVisible,
            showEditer,
            refresh,
            doLockInfo,
            dialogFormAlbumVisible,
            route,
        };
    },
});
